import {
  RiFacebookFill,
  RiInstagramFill,
  RiLinkedinFill,
  RiPhoneLine,
  RiMailLine,
  RiMapPinLine,
} from "@remixicon/react"
import { Link } from "react-router-dom"
import { AgencyLogo } from "@/components/icons/AgencyLogo"
const CURRENT_YEAR = new Date().getFullYear()

const Footer = () => {
  const sections = {
    services: {
      title: "Services",
      items: [
        { label: "Branding & Identity", href: "#services" },
        { label: "Web Development", href: "#services" },
        { label: "SEO Optimization", href: "#services" },
        { label: "Social Media Management", href: "#services" },
        { label: "Digital Advertising", href: "#services" },
        { label: "Analytics & Reporting", href: "#services" },
      ],
    },
    company: {
      title: "Company",
      items: [
        { label: "Why Choose Us", href: "#why-us" },
        { label: "Our Process", href: "#process" },
        { label: "Client Testimonials", href: "#testimonials" },
        { label: "Free Consultation", href: "#contact" },
      ],
    },
    locations: {
      title: "Serving the Lowcountry",
      items: [
        { label: "Charleston, SC", href: "#" },
        { label: "Mount Pleasant", href: "#" },
        { label: "Summerville", href: "#" },
        { label: "North Charleston", href: "#" },
        { label: "West Ashley", href: "#" },
        { label: "James Island", href: "#" },
      ],
    },
  }

  return (
    <footer className="bg-gradient-to-br from-gray-50 via-white to-gray-50 border-t-2 border-gray-200">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:py-20">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-12">
          {/* Brand Section - Takes more space */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block mb-6 transition-transform hover:scale-105">
              <AgencyLogo className="w-28 h-auto" />
              <span className="sr-only">Marketier Agency Logo (go home)</span>
            </Link>
            <p className="text-gray-600 mb-8 leading-relaxed text-base">
              Empowering Charleston businesses to grow online with expert digital marketing services.
            </p>

            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              <a
                href="tel:+18435551234"
                className="flex items-center gap-3 text-gray-700 hover:text-[#06b6d4] transition-colors group"
              >
                <div className="p-2.5 rounded-lg bg-gray-100 group-hover:bg-[#06b6d4]/10 transition-colors">
                  <RiPhoneLine className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Call us</p>
                  <span className="text-sm font-semibold">(843) 555-1234</span>
                </div>
              </a>
              <a
                href="mailto:contact@marketieragency.com"
                className="flex items-center gap-3 text-gray-700 hover:text-[#06b6d4] transition-colors group"
              >
                <div className="p-2.5 rounded-lg bg-gray-100 group-hover:bg-[#06b6d4]/10 transition-colors">
                  <RiMailLine className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Email us</p>
                  <span className="text-sm font-semibold">contact@marketieragency.com</span>
                </div>
              </a>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="p-2.5 rounded-lg bg-gray-100">
                  <RiMapPinLine className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Location</p>
                  <span className="text-sm font-semibold">Charleston, South Carolina</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Follow Us</p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61569324204972"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-[#06b6d4] hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                  aria-label="Follow us on Facebook"
                >
                  <RiFacebookFill className="size-5" />
                </a>
                <a
                  href="https://www.instagram.com/marketieragency/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-[#06b6d4] hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                  aria-label="Follow us on Instagram"
                >
                  <RiInstagramFill className="size-5" />
                </a>
                <a
                  href="https://linkedin.com/company/marketier-agency/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-[#06b6d4] hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                  aria-label="Connect with us on LinkedIn"
                >
                  <RiLinkedinFill className="size-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Navigation Links Grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12">
            {/* Services Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-5 flex items-center gap-2">
                <div className="w-1 h-5 bg-[#06b6d4] rounded-full" />
                {sections.services.title}
              </h3>
              <ul className="space-y-3">
                {sections.services.items.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-gray-600 hover:text-[#06b6d4] transition-colors inline-flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-[#06b6d4] mr-0 group-hover:mr-2 transition-all duration-200 rounded-full" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-5 flex items-center gap-2">
                <div className="w-1 h-5 bg-[#06b6d4] rounded-full" />
                {sections.company.title}
              </h3>
              <ul className="space-y-3">
                {sections.company.items.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-gray-600 hover:text-[#06b6d4] transition-colors inline-flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-[#06b6d4] mr-0 group-hover:mr-2 transition-all duration-200 rounded-full" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Locations Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-5 flex items-center gap-2">
                <div className="w-1 h-5 bg-[#06b6d4] rounded-full" />
                {sections.locations.title}
              </h3>
              <ul className="space-y-3">
                {sections.locations.items.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-gray-600 hover:text-[#06b6d4] transition-colors inline-flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-[#06b6d4] mr-0 group-hover:mr-2 transition-all duration-200 rounded-full" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t-2 border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600 text-center md:text-left">
              &copy; {CURRENT_YEAR} Marketier Agency. All rights reserved. Empowering businesses to grow online.
            </p>
            <div className="flex gap-6 text-sm text-gray-600">
              <a href="#" className="hover:text-[#06b6d4] transition-colors font-medium">
                Privacy Policy
              </a>
              <span className="text-gray-300">|</span>
              <a href="#" className="hover:text-[#06b6d4] transition-colors font-medium">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
